if (!customElements.get('product-card-image-slider')) {
  // One observer for all cards, including cards appended by collection pagination.
  let loopObserver;
  function observeLoop(slider) {
    if (!('IntersectionObserver' in window)) return;
    loopObserver ||= new IntersectionObserver(entries => {
      // Read all geometry before inserting any loop slides.
      const layouts = entries.filter(entry => entry.isIntersecting)
        .map(entry => ({ slider: entry.target, layout: entry.target.measureLoop() }));
      layouts.forEach(({ slider, layout }) => {
        if (layout) slider.ensureLoopSlides(layout);
      });
    }, { rootMargin: '200px 0px' });
    loopObserver.observe(slider);
  }

  class ProductCardImageSlider extends HTMLElement {
    connectedCallback() {
      // Collection pagination moves existing cards. Reconnect their listeners
      // every time, while keeping the slides, loop and active image intact.
      if (!this.initialized) this.initialize();
      if (!this.initialized) return;

      this.connectListeners();

      // New cards already start at image 1; avoid a layout read for every card.
      if ((this.loopReady || this.activeIndex > 0) && window.innerWidth < 768 && this.clientWidth) {
        this.scrollLeft = this.slideOffset(this.slides[this.activeIndex]);
      }
      this.updateDots(this.activeIndex);
    }

    initialize() {
      this.slides = Array.from(this.querySelectorAll('[data-product-card-slide]'));
      this.card = this.closest('.product-card');
      this.dots = Array.from(this.card?.querySelectorAll('[data-product-card-dot]') || []);

      if (this.slides.length < 2 || this.dots.length < 2) return;

      this.initialized = true;
      this.activeIndex = 0;
      this.raf = null;
      this.loopResetTimer = null;
      this.deferredImagesLoaded = false;
      this.loopReady = false;
      this.loopStartSlide = null;
      this.loopEndSlide = null;
      this.pointerActive = false;
      this.touchActive = false;

      this.onScroll = this.handleScroll.bind(this);
      this.onInteractionStart = this.handleInteractionStart.bind(this);
      this.onInteractionEnd = this.handleInteractionEnd.bind(this);
      this.onScrollEnd = this.resetLoop.bind(this);
      this.onCardEnter = () => {
        if (window.innerWidth >= 768) this.loadDesktopImage();
      };
      this.onCardFocus = this.onCardEnter;
    }

    connectListeners() {
      if (this.listening) return;
      this.listening = true;

      this.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('scrollend', this.onScrollEnd, { passive: true });
      this.addEventListener('pointerdown', this.onInteractionStart, { passive: true });
      this.addEventListener('touchstart', this.onInteractionStart, { passive: true });
      this.card.addEventListener('pointerenter', this.onCardEnter, { passive: true });
      this.card.addEventListener('focusin', this.onCardFocus);
      // Prepare only nearby cards, before a swipe starts. Deferred image URLs
      // stay deferred: cloning the slide must not start loading its image.
      if (!this.loopReady) observeLoop(this);
    }

    listenForInteractionEnd() {
      if (this.listeningForEnd) return;
      this.listeningForEnd = true;
      ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach(type => {
        document.addEventListener(type, this.onInteractionEnd, { passive: true });
      });
    }

    stopListeningForInteractionEnd() {
      if (!this.listeningForEnd) return;
      this.listeningForEnd = false;
      ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach(type => {
        document.removeEventListener(type, this.onInteractionEnd);
      });
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('scrollend', this.onScrollEnd);
      this.removeEventListener('pointerdown', this.onInteractionStart);
      this.removeEventListener('touchstart', this.onInteractionStart);
      this.card.removeEventListener('pointerenter', this.onCardEnter);
      this.card.removeEventListener('focusin', this.onCardFocus);
      this.stopListeningForInteractionEnd();
      loopObserver?.unobserve(this);

      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.loopResetTimer) clearTimeout(this.loopResetTimer);
      this.raf = null;
      this.loopResetTimer = null;
      this.pointerActive = false;
      this.touchActive = false;
      this.listening = false;
    }

    handleInteractionStart(event) {
      if (window.innerWidth >= 768) return;
      if (event.type === 'touchstart') this.touchActive = true;
      else this.pointerActive = true;
      this.listenForInteractionEnd();
      clearTimeout(this.loopResetTimer);
      this.loopResetTimer = null;
      this.loadDeferredImages();
    }

    handleInteractionEnd(event) {
      if (!this.pointerActive && !this.touchActive) return;
      if (event.type.startsWith('touch')) this.touchActive = event.touches.length > 0;
      else this.pointerActive = false;
      if (!this.pointerActive && !this.touchActive) {
        this.stopListeningForInteractionEnd();
        this.scheduleLoopReset();
      }
    }

    measureLoop() {
      if (!this.isConnected || this.loopReady || this.slides.length < 2 || window.innerWidth >= 768) return null;
      if (this.pointerActive || this.touchActive) return null;
      const slideWidth = this.clientWidth;
      if (!slideWidth) return null;
      return {
        scrollLeft: this.scrollLeft,
        shift: this.slideOffset(this.slides[1]) - this.slideOffset(this.slides[0])
      };
    }

    ensureLoopSlides(layout = this.measureLoop()) {
      if (!layout || this.loopReady || !this.isConnected) return;
      const firstSlide = this.slides[0];
      const lastSlide = this.slides[this.slides.length - 1];

      // Clone the last real slide before image 1, so the visitor can swipe
      // backwards immediately: 1 -> 3 (or 1 -> 2 when only two images exist).
      const loopStartSlide = lastSlide.cloneNode(true);
      loopStartSlide.removeAttribute('data-product-card-slide');
      loopStartSlide.setAttribute('data-product-card-loop-slide', 'start');
      loopStartSlide.setAttribute('aria-hidden', 'true');

      // Clone image 1 after the last real slide for the forward loop: 3 -> 1.
      const loopEndSlide = firstSlide.cloneNode(true);
      loopEndSlide.removeAttribute('data-product-card-slide');
      loopEndSlide.setAttribute('data-product-card-loop-slide', 'end');
      loopEndSlide.setAttribute('aria-hidden', 'true');

      this.prepend(loopStartSlide);
      this.appendChild(loopEndSlide);

      this.loopStartSlide = loopStartSlide;
      this.loopEndSlide = loopEndSlide;
      this.loopReady = true;

      // Prepending one slide shifts every real slide one position to the right.
      // Correct it before interaction, without changing an active swipe.
      this.scrollLeft = layout.scrollLeft + layout.shift;
      loopObserver?.unobserve(this);
    }

    slideOffset(slide) {
      return slide.offsetLeft - this.firstElementChild.offsetLeft;
    }

    handleScroll() {
      if (window.innerWidth >= 768 || this.raf) return;

      this.raf = requestAnimationFrame(() => {
        this.raf = null;

        const slideWidth = this.clientWidth;
        if (!slideWidth) return;

        // Fallback for any programmatic scroll that happens before interaction.
        const initialOffset = this.loopReady ? this.slideOffset(this.slides[0]) : 0;
        if (!this.deferredImagesLoaded && Math.abs(this.scrollLeft - initialOffset) > 4) {
          this.loadDeferredImages();
        }

        const rawIndex = Math.max(0, Math.round(this.scrollLeft / slideWidth));
        let index = 0;

        if (this.loopReady) {
          // DOM order after loop setup:
          // [clone last] [real 1] [real 2] [real 3] [clone 1]
          if (rawIndex <= 0) {
            index = this.slides.length - 1;
          } else if (rawIndex >= this.slides.length + 1) {
            index = 0;
          } else {
            index = rawIndex - 1;
          }
        } else {
          index = Math.min(this.slides.length - 1, rawIndex);
        }

        if (index !== this.activeIndex) {
          this.activeIndex = index;
          this.updateDots(index);
        }

        this.scheduleLoopReset();
      });
    }

    scheduleLoopReset() {
      if (this.loopResetTimer) clearTimeout(this.loopResetTimer);
      this.loopResetTimer = null;
      if (this.pointerActive || this.touchActive) return;

      // Fallback for browsers without scrollend. Every movement restarts the
      // timer; holding a finger still must never trigger a loop jump.
      this.loopResetTimer = setTimeout(() => {
        this.loopResetTimer = null;
        this.resetLoop();
      }, 180);
    }

    resetLoop() {
      if (window.innerWidth >= 768 || this.pointerActive || this.touchActive) return;
      const slideWidth = this.clientWidth;
      if (!slideWidth) return;
      if (!this.loopReady) {
        this.ensureLoopSlides();
        return;
      }

      // A rounded slide index can still be halfway through a swipe. Only
      // reposition when the matching clone is fully aligned (within 1px).
      let index;
      if (Math.abs(this.scrollLeft - this.slideOffset(this.loopStartSlide)) <= 1) index = this.slides.length - 1;
      else if (Math.abs(this.scrollLeft - this.slideOffset(this.loopEndSlide)) <= 1) index = 0;
      else return;

      this.scrollTo({ left: this.slideOffset(this.slides[index]), top: 0, behavior: 'auto' });
      this.activeIndex = index;
      this.updateDots(index);
    }

    loadDeferredImages() {
      const images = this.querySelectorAll('img[data-src]');
      if (!images.length) {
        this.deferredImagesLoaded = true;
        return;
      }

      images.forEach((image) => this.loadImage(image));

      this.deferredImagesLoaded = true;
    }

    loadDesktopImage() {
      const secondSlide = this.slides[1];
      if (secondSlide?.classList.contains('product-card__image--mobile-only')) return;
      const image = secondSlide?.querySelector('img[data-src]');
      if (image) this.loadImage(image);
    }

    loadImage(image) {
      // Set sizes first: assigning srcset before sizes can start an oversized
      // request. A photo requested by a gesture should load immediately.
      const reveal = () => image.parentElement?.removeAttribute('data-secondary-pending');
      if (image.parentElement?.hasAttribute('data-secondary-pending')) {
        image.addEventListener('load', reveal, { once: true });
      }
      image.loading = 'eager';
      if (image.dataset.sizes) image.sizes = image.dataset.sizes;
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (image.dataset.src) image.src = image.dataset.src;
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-sizes');
      image.removeAttribute('data-src');
      if (image.complete && image.naturalWidth > 0) reveal();
    }

    updateDots(index) {
      this.dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
      });
    }
  }

  customElements.define('product-card-image-slider', ProductCardImageSlider);
}
